/**
 * A ficha de um título — o que a gaveta "Ver lançamento" mostra.
 *
 * É uma FOLHA DE LEITURA, não um formulário, na mesma forma da
 * `FichaNotaFiscal`: cabeçalho identificando o documento, um bloco de
 * situação, seções de campo e os valores fechando embaixo. Quem abre esta
 * gaveta está conferindo um título contra um boleto ou um recibo na mão, não
 * auditando coluna a coluna.
 *
 * Ela lê a LINHA que a listagem já tem, e não faz um GET próprio: o contrato
 * desta rodada não criou `GET /financeiro/{lado}/{codigo}`, e a linha da
 * listagem já carrega o título inteiro. Uma chamada a mais para reler o que
 * está na mão é latência sem informação nova.
 *
 * O rodapé só oferece o que a regra permite. Quando não permite, a RAZÃO fica
 * escrita — e ela vem do SERVIDOR (`motivo_bloqueio`), a mesma frase que o
 * menu da linha mostra. O front não recalcula a regra: recalcular seria ter
 * duas cópias dela, e a segunda diverge na primeira mudança.
 */
import type { ReactNode } from 'react'
import { Botao } from '../../componentes/Botao'

import { Icone } from '../../componentes/Icone'
import { formatar, formatarMoeda } from '../../plataforma/dinheiro'
import {
  apoioDaSituacao,
  bloqueioDeEdicao,
  origemDaConta,
  urgenciaDaConta,
  type Lado,
  type LinhaConta,
} from '../../plataforma/financeiro'
import { dataISOparaBR } from '../../plataforma/mascaras'

import { SeloDaConta } from './SeloDaConta'

const ROTULO = 'text-[11px] font-bold tracking-[0.6px] uppercase text-texto-suave'

export function FichaDaConta({
  lado,
  linha,
  aoFechar,
  aoBaixar,
  aoEditar,
}: {
  lado: Lado
  linha: LinhaConta
  aoFechar: () => void
  aoBaixar: () => void
  aoEditar: () => void
}) {
  const pessoa = lado === 'receber' ? linha.cliente : linha.fornecedor
  const urgencia = urgenciaDaConta(linha)
  const impedido = bloqueioDeEdicao(linha)
  const origem = origemDaConta(linha)
  const aberta = urgencia !== 'quitada' && urgencia !== 'cancelada'

  return (
    <div className="flex flex-col">
      {/*
        Sem cabeçalho próprio: quem identifica o documento é o
        `CabecalhoDaGaveta`, que já mostra a descrição e o número. Este bloco
        repetia os dois dois centímetros abaixo — e texto repetido faz a pessoa
        procurar a diferença entre as duas cópias.

        O que SOBROU aqui é o que o cabeçalho não cabe: a pessoa e a parcela.
      */}
      <p className="pb-4 text-[13px] text-texto-suave">
        {pessoa ? (
          <>
            {pessoa.nome} · <span className="tabular">{pessoa.codigo}</span>
          </>
        ) : lado === 'receber' ? (
          'Sem cliente'
        ) : (
          'Sem fornecedor'
        )}
        {linha.total_parcelas > 1 && (
          <>
            {' '}
            · <span className="tabular">parcela {linha.numero_parcela}/{linha.total_parcelas}</span>
          </>
        )}
      </p>

      <div className="flex flex-col gap-4 border-t border-borda pt-4">
        {/* ── situação: em que pé está o título ── */}
        <div className="rounded-sistema border border-borda bg-fundo px-4 py-3.5">
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <div>
              <p className={ROTULO}>Situação</p>
              <div className="mt-1">
                <SeloDaConta lado={lado} linha={linha} />
              </div>
              <p className="mt-1.5 text-[12.5px] text-texto-suave">
                {apoioDaSituacao(linha, lado)}
              </p>
            </div>
            <div>
              <p className={ROTULO}>Emissão</p>
              <p className="tabular mt-1 text-[14px] font-semibold text-tinta">
                {dataISOparaBR(linha.emissao)}
              </p>
            </div>
            <div>
              <p className={ROTULO}>Vencimento</p>
              <p
                className={`tabular mt-1 text-[14px] font-semibold ${
                  urgencia === 'atrasada' ? 'text-erro' : 'text-tinta'
                }`}
              >
                {dataISOparaBR(linha.vencimento)}
              </p>
            </div>
          </div>
        </div>

        {/* ── de quem, e de quê ── */}
        <Secao titulo="De quem, e de quê">
          <Linha rotulo={lado === 'receber' ? 'Cliente' : 'Fornecedor'}>
            {pessoa ? `${pessoa.nome} · ${pessoa.codigo}` : '—'}
          </Linha>
          <Linha rotulo="Categoria">
            {linha.categoria ? linha.categoria.nome : 'Sem categoria'}
          </Linha>
          {/* A descrição NÃO se repete aqui: ela é o título da gaveta, logo
              acima. Campo repetido dois centímetros abaixo do próprio valor
              faz a pessoa procurar a diferença entre os dois. */}
          <Linha rotulo="Documento">
            <span className="tabular">{linha.numero_documento}</span>
          </Linha>
        </Secao>

        {/* ── como o dinheiro entra ── */}
        <Secao titulo={lado === 'receber' ? 'Como o dinheiro entra' : 'Como o dinheiro sai'}>
          <Linha rotulo="Forma de pagamento">{linha.forma_rotulo ?? linha.forma ?? '—'}</Linha>
          <Linha rotulo="Conta prevista">
            {linha.conta_financeira ? linha.conta_financeira.nome : 'Definir na baixa'}
          </Linha>
        </Secao>

        {linha.observacao && (
          <Secao titulo="Observações">
            <p className="text-[13px] leading-[1.55] whitespace-pre-line text-texto">
              {linha.observacao}
            </p>
          </Secao>
        )}

        {/* ── origem: só aparece quando o título veio de um documento ── */}
        {origem !== 'manual' && (
          <Secao titulo="Origem">
            <Linha rotulo={origem === 'venda' ? 'Venda' : 'Nota fiscal'}>
              <span className="tabular">{linha.venda ?? linha.nota_fiscal}</span>
            </Linha>
            <p className="mt-1 text-[12.5px] leading-[1.5] text-texto-suave">
              Título gerado por documento não se edita nem se exclui — corrige-se o documento.
            </p>
          </Secao>
        )}

        {/* ── valores: bruto, desconto, acréscimo e o líquido fechando ── */}
        <div className="rounded-sistema border border-borda bg-fundo px-4 py-3.5">
          <LinhaDeValor rotulo="Valor bruto" valor={formatar(linha.valor_bruto_centavos)} />
          {linha.desconto_centavos > 0 && (
            <LinhaDeValor rotulo="Desconto" valor={`− ${formatar(linha.desconto_centavos)}`} />
          )}
          {linha.acrescimo_centavos > 0 && (
            <LinhaDeValor rotulo="Acréscimo" valor={`+ ${formatar(linha.acrescimo_centavos)}`} />
          )}
          <div className="mt-2.5 flex items-baseline justify-between gap-3 border-t border-borda pt-2.5">
            <span className={ROTULO}>Valor líquido</span>
            <span className="tabular font-titulo text-[22px] font-bold text-titulo">
              {formatarMoeda(linha.valor_centavos)}
            </span>
          </div>
        </div>

        {/* ── histórico da baixa ── */}
        {linha.baixada_em && (
          <div className="flex flex-col gap-1.5 rounded-sistema bg-ok-suave px-4 py-3 text-[13px] text-ok-texto">
            <div className="flex items-center gap-2 font-semibold">
              <Icone nome="check_circle" tamanho={18} />
              {lado === 'receber' ? 'Recebida' : 'Paga'} em{' '}
              <span className="tabular">{dataISOparaBR(linha.baixada_em)}</span>
            </div>
            <p>
              {formatarMoeda(linha.valor_baixado_centavos ?? linha.valor_centavos)}
              {linha.valor_baixado_centavos !== undefined &&
                linha.valor_baixado_centavos !== linha.valor_centavos && (
                  <>
                    {' '}
                    · previsto {formatar(linha.valor_centavos)} (
                    {linha.valor_baixado_centavos > linha.valor_centavos
                      ? `${formatar(linha.valor_baixado_centavos - linha.valor_centavos)} a mais`
                      : `${formatar(linha.valor_centavos - linha.valor_baixado_centavos)} a menos`}
                    )
                  </>
                )}
              {/* Quem deu a baixa não aparece: a API não devolve o operador
                  nesta rodada, e um "por —" seria pior que a ausência. */}
            </p>
          </div>
        )}
      </div>

      {/* ── a decisão, no rodapé ── */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-borda pt-4">
        <p className="max-w-[42ch] text-[12.5px] text-texto-suave">
          {impedido
            ? `Não dá para editar: ${impedido}.`
            : 'Título lançado à mão — dá para editar e excluir enquanto não tiver baixa.'}
        </p>
        <div className="flex shrink-0 items-center gap-2.5">
          <Botao variante="secundario" onClick={aoFechar}>
            Fechar
          </Botao>
          <Botao variante="secundario" disabled={!!impedido} onClick={aoEditar}>
            Editar
          </Botao>
          {aberta && (
            <Botao icone="payments" onClick={aoBaixar}>
              {lado === 'receber' ? 'Receber…' : 'Pagar…'}
            </Botao>
          )}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ peças */

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div>
      <p className={`${ROTULO} mb-1.5`}>{titulo}</p>
      <div className="rounded-sistema border border-divisoria px-4 py-1">{children}</div>
    </div>
  )
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-divisoria py-2.5 first:border-t-0">
      <span className="shrink-0 text-[12.5px] text-texto-suave">{rotulo}</span>
      <span className="min-w-0 text-right text-[13.5px] font-medium text-tinta">{children}</span>
    </div>
  )
}

function LinhaDeValor({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-[3px] text-[13px]">
      <span className="text-texto-suave">{rotulo}</span>
      <span className="tabular text-tinta">{valor}</span>
    </div>
  )
}
